import React, { useCallback, useEffect, useRef, useState } from 'react';
import { identity, compose, prop } from 'ramda';

import { clsx } from 'js/app/utils/clsx.util';
import { clsb } from 'js/utils/classNames.util';
import { useTranslations } from 'js/hooks/i18n';
import { useDeviceSelector } from 'js/store/hooks';
import { SvgIcon } from 'js/app/components/common/SvgIcon';
import { Control } from 'js/components/common/Fields/Control/Control';
import ValidationIcon from 'js/components/common/Fields/Icons/ValidationIcon';
import { SELECT_ARROW_ICON } from 'js/components/common/Fields/Select/constants.buzz.js';

import withField from '../withField';

import style from './Select.scss';

export const SelectBare = ({
    /* rff render props */
    input,
    meta = {},

    /* user provided props */
    id,
    name,
    label,
    hint,
    placeholder = '',
    className,
    classNames,
    options,
    disabled,
    readOnly,
    setValue,
    parse,
    onMouseDown,
    infoTooltip,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,

    /* buzz props */
    isValidationIcon,
    resetFieldState = identity,
    handlerParsedData,
    /* end buzz props */

    ...selectDomProps
}) => {
    const selectRef = useRef();
    const translate = useTranslations();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const preventClick = useCallback(event => event.preventDefault(), []);

    useEffect(() => {
        if (placeholder && !input.value || !options.length || !setValue) {
            return;
        }

        const isValueMissing = options.every(item => item.value.toString() !== input.value.toString());

        if (isValueMissing) {
            const newValue = parse ? parse(options[0].value) : options[0].value;

            setValue(newValue);
        }
    }, [input.value, options, parse, setValue, placeholder]);

    useEffect(() => {
        if (isDesktop && !disabled && meta.active) {
            selectRef?.current?.focus();
        }
    }, [isDesktop, disabled, meta.active]);

    /* buzz */
    const buildCls = clsb([style, classNames]);
    const [parsedData, setParsedData] = useState(null);

    useEffect(() => {
        if (handlerParsedData) {
            setParsedData(input.value
                ? handlerParsedData(options[input.value].label)
                : null
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input.value]);
    /* end buzz */

    return (
        <Control
            {...{ id, meta, className, label, infoTooltip, isLabelAsteriskEnabled, hint, readOnly, animate: true }}
            classNames={{
                control: clsx(style.control, classNames.control),
                content: clsx(
                    style.content,
                    classNames.content,
                    { [style.isParseData]: handlerParsedData, [style.disabled]: disabled || readOnly }
                ),
                label: style.label,
                ...classNames
            }}
        >
            <div className={style.wrap}>
                <select
                    id={id}
                    ref={selectRef}
                    tabIndex={disabled || readOnly ? -1 : 0}
                    className={clsx(buildCls(['select']), { [style.empty]: placeholder && (input.value == null || input.value === '') })}
                    name={name}
                    {...input}
                    {...selectDomProps}
                    aria-label={selectDomProps['aria-label'] || translate('playtech.mobile.system.select.aria-label')}
                    {...meta.submitting ? { disabled: true } : {}}
                    disabled={disabled}
                    onMouseDown={readOnly ? preventClick : onMouseDown}
                    data-testid="Select_select"
                >
                    {(placeholder || isPlaceholderAsteriskEnabled) && (
                        <option disabled value="" className={clsx(style.option, style.hide)}>
                            {`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                        </option>
                    )}
                    {options.map(item => (
                        <option
                            key={item.value}
                            value={item.value}
                            disabled={item.disabled}
                            className={clsx(style.option, classNames.option)}
                            data-testid="Select_option"
                        >
                            {item.label}
                        </option>
                    ))}
                </select>

                {/* buzz add isValidationIcon and onClick  */}
                {isValidationIcon && (meta.touched || input.value) && !meta.active && !readOnly && (
                    <ValidationIcon
                        className={buildCls(['validationIcon'])}
                        isValid={!meta.error}
                        animate
                        onClick={() => resetFieldState(name)}
                        aria-label={meta.error}
                        tabIndex={meta.error ? 0 : -1}
                    />
                )}

                {/* buzz */}
                <SvgIcon type={SELECT_ARROW_ICON} className={style.parsedDataArrow} />
            </div>
            {/* buzz  */}
            {handlerParsedData &&
                <div className={style.parsedDataWrap}>
                    <div className={style.parsedData}>
                        {parsedData || placeholder}
                    </div>
                    <SvgIcon type={SELECT_ARROW_ICON} className={style.parsedDataArrow} />
                </div>
            }
        </Control>
    );
};

SelectBare.defaultProps = {
    options: [],
    className: '',
    classNames: {},
    placeholder: null
};

export const withFieldProps = Wrapped => props =>
    <Wrapped
        enableSelfValueChange={true}
        {...props}
    />;

const enhance = compose(
    withFieldProps,
    withField
);

export const Select = enhance(SelectBare);
